/**
 * The one card that offers Web Push: what this browser can do about it right
 * now, in `src/lib/push.ts`'s own words, and the one button that changes it.
 *
 * It draws nothing (`describePush` returns `null`) for a browser that cannot
 * do this at all — an unsupported one, or a deployment with no VAPID keys —
 * because a card explaining that a browser cannot do something is a card
 * about this app, not about the person reading it.
 */

import { useCallback, useEffect, useState } from 'react'
import { availability, describePush, type PushState } from '../lib/push'
import { readPushEnvironment, subscribeToPush, unsubscribeFromPush } from '../push-document'

export function PushCard() {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    const env = await readPushEnvironment()
    setState(availability(env))
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function act() {
    if (state === null) return
    setBusy(true)
    setError(null)
    try {
      if (state === 'subscribed') await unsubscribeFromPush()
      else await subscribeToPush()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'that did not work')
    } finally {
      setBusy(false)
    }
  }

  if (state === null) return null
  const copy = describePush(state)
  if (!copy) return null

  return (
    <section className="card push-card">
      <h3>{copy.headline}</h3>
      <p className="muted">{copy.detail}</p>
      {error && <p className="bad note">{error}</p>}
      {copy.action && (
        <p className="actions">
          <button type="button" disabled={busy} onClick={() => void act()}>
            {busy ? 'Working…' : copy.action}
          </button>
        </p>
      )}
    </section>
  )
}
